import { useQueries, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import type { HwcgNoticeStepReportDto } from 'components/IncidentConfig/CreateIncident/hwcgNoticeStepReportModel';
import apiConfig from 'constants/apiConfig';
import { selectGroupGuid } from 'slices/commonSelectors';

/** Org-scoped HWCG step-1 + step-2 response board for a confidentiality notice. */
export function useHwcgNoticeStepReport(
  step1NotificationId: string | null,
  options?: { enabled?: boolean }
) {
  const groupGuid = useSelector(selectGroupGuid);
  return useQuery<HwcgNoticeStepReportDto>({
    queryKey: ['hwcgNoticeStepReport', step1NotificationId, groupGuid],
    enabled:
      Boolean(step1NotificationId) &&
      Boolean(groupGuid) &&
      options?.enabled !== false,
    refetchInterval: 45_000,
    queryFn: async () => {
      const response = await axios.get(
        `${apiConfig.apiGateway.notifications}/sends/${step1NotificationId}/hwcg-steps`,
        { params: { groupId: groupGuid } }
      );
      return response.data;
    },
  });
}

/** Fetch hwcg-steps for each unique parent notification id (itemized sends). */
export function useHwcgNoticeStepReports(
  notificationIds: string[],
  options?: { enabled?: boolean }
) {
  const groupGuid = useSelector(selectGroupGuid);
  const uniqueIds = useMemo(
    () => [...new Set(notificationIds.filter(Boolean))],
    [notificationIds]
  );
  const queries = useQueries({
    queries: uniqueIds.map((notificationId) => ({
      queryKey: ['hwcgNoticeStepReport', notificationId, groupGuid] as const,
      enabled:
        Boolean(notificationId) &&
        Boolean(groupGuid) &&
        options?.enabled !== false,
      refetchInterval: 45_000,
      queryFn: async (): Promise<HwcgNoticeStepReportDto> => {
        const response = await axios.get(
          `${apiConfig.apiGateway.notifications}/sends/${notificationId}/hwcg-steps`,
          { params: { groupId: groupGuid } }
        );
        return response.data;
      },
    })),
  });

  const reportsByNotificationId = useMemo(() => {
    const map: Record<string, HwcgNoticeStepReportDto | undefined> = {};
    uniqueIds.forEach((id, index) => {
      map[id] = queries[index]?.data;
    });
    return map;
  }, [queries, uniqueIds]);

  return {
    reportsByNotificationId,
    isLoading: queries.some((query) => query.isLoading),
    isError: queries.some((query) => query.isError),
  };
}
